import React, { useEffect, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Clock3,
  CalendarDays,
  MoreVertical,
  Video,
  Phone,
  Headphones,
  ChevronRight,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  Link as LinkIcon,
  User,
  Mail,
  Phone as PhoneIcon,
  MapPin,
  ArrowLeft,
} from "lucide-react";
import { getAppointments, getAppointmentDetails } from "../../api/api";
import AppoinmentModal from "../AppoinmnetModal";
import SessionNotesPopup from "../SessionNotesPopup";
import AppointmentDetails from "./AppointmentDetails"; // Import AppointmentDetails

const MyAppointments = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("Upcoming");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [allAppointments, setAllAppointments] = useState([]);
  const [imageErrors, setImageErrors] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isNotesPopupOpen, setIsNotesPopupOpen] = useState(false);
  const [appointmentDetails, setAppointmentDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  
  // States for showing appointment details
  const [showDetails, setShowDetails] = useState(false);
  const [selectedAppointmentForDetail, setSelectedAppointmentForDetail] = useState(null);

  const fetchAllAppointments = async () => {
    try {
      setLoading(true);
      const response = await getAppointments(1, 100);
      if (response && response.status === true) {
        let allData = [...(response.data || [])];
        const totalPages = response.totalPages || 1;
        if (totalPages > 1) {
          const promises = [];
          for (let page = 2; page <= totalPages; page++) {
            promises.push(getAppointments(page, 100));
          }
          const remainingResponses = await Promise.all(promises);
          remainingResponses.forEach((res) => {
            if (res && res.status === true && res.data) {
              allData = [...allData, ...res.data];
            }
          });
        }
        setAllAppointments(allData);
      } else {
        console.error(response?.message || "Failed to load appointments");
        setAllAppointments([]);
      }
    } catch (error) {
      console.error("Fetch appointments error:", error);
      setAllAppointments([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAllAppointments();
  }, []);

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    fetchAllAppointments();
  }, []);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    fetchAllAppointments();
  };

  // Updated: Show details within the component instead of navigating
  const handleViewDetails = (appointment, e) => {
    e?.stopPropagation();
    setSelectedAppointmentForDetail(appointment);
    setShowDetails(true);
  };

  // Go back to appointments list
  const handleBackToList = () => {
    setShowDetails(false);
    setSelectedAppointmentForDetail(null);
  };

  const handleStartCall = (appointment, e) => {
    e?.stopPropagation();
    const mode = appointment.mode_consultation?.toLowerCase() || "";
    if (mode.includes("video")) {
      if (!appointment.video_link) {
        alert("Video call link not available yet");
        return;
      }
      window.open(appointment.video_link, "_blank");
    } else if (mode.includes("voice") || mode.includes("audio")) {
      if (!appointment.phone_number) {
        alert("Audio call number not available yet");
        return;
      }
      window.location.href = `tel:${appointment.phone_number}`;
    } else {
      alert("Consultation type not supported");
    }
  };

  const handleReschedule = (appointment, e) => {
    e?.stopPropagation();

  };

  const handleViewNotes = async (appointment, e) => {
    e?.stopPropagation();
    
    setSelectedAppointment(appointment);
    setIsNotesPopupOpen(true);
    setDetailsLoading(true);
    
    try {
      const response = await getAppointmentDetails(appointment.appointmentToken);
      
      if (response && response.status === true) {
        setAppointmentDetails(response);
      } else {
        console.error("Failed to fetch appointment details");
        setAppointmentDetails(null);
      }
    } catch (error) {
      console.error("Error fetching appointment details:", error);
      setAppointmentDetails(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  const isCallEnabled = (appointment) => {
    const mode = appointment.mode_consultation?.toLowerCase() || "";
    if (mode.includes("video")) {
      return appointment.video_link && appointment.video_link.trim() !== "";
    } else if (mode.includes("voice") || mode.includes("audio")) {
      return appointment.phone_number && appointment.phone_number.trim() !== "";
    }
    return false;
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.charAt(0).toUpperCase();
  };

  const formatName = (name) => {
    if (!name) return "Patient";
    return name
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const capitalizeWords = (str) => {
    if (!str) return "";
    return str
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const getRandomColor = (name) => {
    const colors = [
      "#18B58A",
      "#3A7AFE",
      "#FF8C00",
      "#FF4444",
      "#9C27B0",
      "#E91E63",
      "#00BCD4",
      "#4CAF50",
      "#FF9800",
      "#795548",
      "#607D8B",
      "#9E9E9E",
    ];

    if (!name) return colors[0];
    const index =
      name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) %
      colors.length;

    return colors[index];
  };

  const getFilteredAppointments = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    switch (activeTab) {
      case "Upcoming":
        return allAppointments.filter((a) => {
          const isUpcomingStatus = a.status === 0 || a.status === 1;
          if (a.appointment_date) {
            const appointmentDate = new Date(a.appointment_date);
            appointmentDate.setHours(0, 0, 0, 0);
            const isTodayOrFuture = appointmentDate >= today;
            return isUpcomingStatus && isTodayOrFuture;
          }
          return isUpcomingStatus;
        });

      case "Completed":
        return allAppointments.filter((a) => {
          const isCompletedStatus = a.status === 2;
          if (a.appointment_date) {
            const appointmentDate = new Date(a.appointment_date);
            appointmentDate.setHours(0, 0, 0, 0);
            const isPast = appointmentDate < today;
            return isCompletedStatus || isPast;
          }
          return isCompletedStatus;
        });
      case "Cancelled":
        return allAppointments.filter((a) => a.status === 3);
      default:
        return [];
    }
  };

  const getFollowupStatusDisplay = (followupStatus) => {
    const statusMap = {
      pending: { text: "Follow-up Pending", color: "#FF8C00", icon: Clock },
      completed: {
        text: "Follow-up Completed",
        color: "#18B58A",
        icon: CheckCircle,
      },
      scheduled: {
        text: "Follow-up Scheduled",
        color: "#3A7AFE",
        icon: Calendar,
      },
      cancelled: {
        text: "Follow-up Cancelled",
        color: "#FF4444",
        icon: XCircle,
      },
    };

    return (
      statusMap[followupStatus?.toLowerCase()] || {
        text: "No Follow-up",
        color: "#999",
        icon: AlertCircle,
      }
    );
  };

  const getPaymentStatusDisplay = (paymentStatus) => {
    const statusMap = {
      pending: { text: "Payment Pending", color: "#FF8C00", icon: Clock },
      success: {
        text: "Payment Completed",
        color: "#18B58A",
        icon: CheckCircle,
      },
      failed: { text: "Payment Failed", color: "#FF4444", icon: XCircle },
      refunded: { text: "Payment Refunded", color: "#3A7AFE", icon: RefreshCw },
    };

    return (
      statusMap[paymentStatus?.toLowerCase()] || {
        text: "Payment Unknown",
        color: "#999",
        icon: AlertCircle,
      }
    );
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString || dateString === "Text") return "TODAY";

    try {
      const date = new Date(dateString);
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      if (date.toDateString() === today.toDateString()) {
        return "TODAY";
      } else if (date.toDateString() === tomorrow.toDateString()) {
        return "TOMORROW";
      } else {
        return date
          .toLocaleDateString("en-US", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })
          .toUpperCase();
      }
    } catch (error) {
      return dateString;
    }
  };

  const formatTime = (timeString) => {
    if (!timeString || timeString === "Text") return "10:00 AM";
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getConsultationType = (appointment) => {
    const mode = appointment.mode_consultation?.toLowerCase() || "";
    if (mode.includes("video")) return "Video Consultation";
    if (mode.includes("voice") || mode.includes("audio")) return "Voice Consultation";
    return "Consultation";
  };

  const filteredAppointments = getFilteredAppointments();

  const groupedAppointments = filteredAppointments.reduce(
    (groups, appointment) => {
      const date = formatDisplayDate(appointment.appointment_date);
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(appointment);
      return groups;
    },
    {},
  );

  const sortedDates = Object.keys(groupedAppointments).sort((a, b) => {
    if (a === "TODAY") return -1;
    if (b === "TODAY") return 1;
    if (a === "TOMORROW") return -1;
    if (b === "TOMORROW") return 1;
    return new Date(a) - new Date(b);
  });

  const getTabCount = (tab) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    switch (tab) {
      case "Upcoming":
        return allAppointments.filter((a) => {
          const isUpcomingStatus = a.status === 0 || a.status === 1;
          if (a.appointment_date && a.appointment_date !== "Text") {
            const appointmentDate = new Date(a.appointment_date);
            appointmentDate.setHours(0, 0, 0, 0);
            const isTodayOrFuture = appointmentDate >= today;
            return isUpcomingStatus && isTodayOrFuture;
          }
          return isUpcomingStatus;
        }).length;

      case "Completed":
        return allAppointments.filter((a) => {
          const isCompletedStatus = a.status === 2;
          if (a.appointment_date && a.appointment_date !== "Text") {
            const appointmentDate = new Date(a.appointment_date);
            appointmentDate.setHours(0, 0, 0, 0);
            const isPast = appointmentDate < today;
            return isCompletedStatus || isPast;
          }
          return isCompletedStatus;
        }).length;
      case "Cancelled":
        return allAppointments.filter((a) => a.status === 3).length;
      default:
        return 0;
    }
  };

  // If showing details, render AppointmentDetails
  if (showDetails && selectedAppointmentForDetail) {
    return (
      <div className="w-full font-lato">
        <button
          onClick={handleBackToList}
          className="mb-4 flex items-center gap-2 text-[#57b26a] hover:underline text-sm font-medium transition-colors"
        >
          <ArrowLeft size={18} />
        </button>
        <AppointmentDetails appointmentToken={selectedAppointmentForDetail.appointmentToken} />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="w-full font-lato">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-[34px] font-semibold text-[#22302b] leading-tight">
              My Appointments
            </h2>
            <p className="text-[14px] text-[#9aa39d] mt-2">
              Manage your upcoming consultations and review past sessions.
            </p>
          </div>
        </div>

        <div className="flex gap-8 border-b border-[#e8ece8] mb-7">
          {["Upcoming", "Completed", "Cancelled"].map((tab) => (
            <button
              key={tab}
              className="text-[14px] font-medium pb-3 text-[#a0a8a2]"
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#57b26a]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full font-lato">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8">
        <div>
          <h2 className="text-[34px] font-semibold text-[#22302b] leading-tight">
            My Appointments
          </h2>
          <p className="text-[14px] text-[#9aa39d] mt-2">
            Manage your upcoming consultations and review past sessions.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            className="h-[42px] px-5 rounded-md bg-[#57b26a] hover:bg-[#49aa5d] text-white text-[13px] font-medium flex items-center gap-2"
            onClick={handleOpenModal}
          >
            <Plus size={15} />
            Book New Appointment
          </button>
        </div>
      </div>

      <div className="flex gap-8 border-b border-[#e8ece8] mb-7 overflow-x-auto">
        {["Upcoming", "Completed", "Cancelled"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`text-[14px] font-medium pb-3 whitespace-nowrap transition-all ${
              activeTab === tab
                ? "text-[#22302b] border-b-2 border-[#56b96a]"
                : "text-[#a0a8a2] hover:text-[#22302b]"
            }`}
          >
            {tab} ({getTabCount(tab)})
          </button>
        ))}
      </div>

      {filteredAppointments.length === 0 ? (
        <div className="bg-white border border-[#e8ece8] rounded-xl p-12 text-center">
          <div className="w-16 h-16 bg-[#f7f9f7] rounded-full flex items-center justify-center mx-auto mb-4">
            <CalendarDays size={24} className="text-[#9aa39d]" />
          </div>

          <h3 className="text-[18px] font-semibold text-[#22302b] mb-2">
            No {activeTab.toLowerCase()} appointments
          </h3>
          <p className="text-[14px] text-[#9aa39d] mb-4">
            {activeTab === "Upcoming"
              ? "You don't have any upcoming appointments scheduled."
              : activeTab === "Completed"
                ? "You haven't completed any sessions yet."
                : "No canceled sessions to show."}
          </p>

          {activeTab === "Upcoming" && (
            <button
              className="h-[42px] px-5 rounded-md bg-[#57b26a] hover:bg-[#49aa5d] text-white text-[13px] font-medium flex items-center gap-2 mx-auto"
              onClick={handleOpenModal}
            >
              <Plus size={15} />
              Book New Session
            </button>
          )}
        </div>
      ) : (
        <>
          {sortedDates.map((date) => (
            <div key={date} className="mb-8">
              <h3 className="text-[12px] font-semibold text-[#999] mb-3 uppercase tracking-wide">
                {date}
              </h3>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                {groupedAppointments[date].map((appointment) => {
                  const callEnabled = isCallEnabled(appointment);
                  const isCompleted = appointment.status === 2;
                  const isCancelled = appointment.status === 3;

                  return (
                    <div
                      key={appointment.id}
                      className="bg-white border border-[#e8ece8] rounded-xl p-4 shadow-sm"
                    >
                      <div className="flex justify-between items-start mb-4">
                        <span className="bg-[#ffe8b3] text-[#8a6200] text-[11px] px-3 py-1 rounded-full font-medium">
                          {appointment.appointment_date === "Text"
                            ? "Starts today"
                            : formatDisplayDate(appointment.appointment_date)}
                        </span>
                        <button
                          onClick={(e) => handleViewDetails(appointment, e)}
                          className="p-1 hover:bg-gray-100 rounded-full transition"
                          title="View appointment details"
                        >
                          <MoreVertical size={17} className="text-[#9aa39d] hover:text-[#22302b]" />
                        </button>
                      </div>

                      <div className="flex items-center gap-3 mb-4">
                        <img
                          src={appointment.profile_img}
                          alt={appointment.name}
                          className="w-10 h-10 rounded-full object-cover"
                          onError={(e) => {
                            e.target.src = "https://via.placeholder.com/40";
                          }}
                        />
                        <div>
                          <h4 className="text-[14px] font-semibold text-[#22302b]">
                            {capitalizeWords(appointment.purpose) || "Consultation"}
                          </h4>
                          <p className="text-[12px] text-[#9aa39d] mt-1">
                            {capitalizeWords(appointment.mode_consultation)} • {capitalizeWords(appointment.type) || "Consultation"}
                          </p>
                        </div>
                      </div>

                      <div className="bg-[#f7f9f7] rounded-md p-3 flex justify-between mb-4">
                        <div>
                          <p className="text-[10px] text-gray-400">Date</p>
                          <div className="flex gap-2 items-center mt-1">
                            <CalendarDays size={14} className="text-[#57b26a]" />
                            <span className="text-[12px] text-gray-700">
                              {appointment.appointment_date === "Text"
                                ? "N/A"
                                : formatDisplayDate(appointment.appointment_date)}
                            </span>
                          </div>
                        </div>
                        <div>
                          <p className="text-[10px] text-gray-400">Time</p>
                          <div className="flex gap-2 items-center mt-1">
                            <Clock3 size={14} className="text-[#57b26a]" />
                            <span className="text-[12px] text-gray-700">
                              {formatTime(appointment.appointment_time)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {(isCompleted || isCancelled) ? (
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            onClick={(e) => handleViewNotes(appointment, e)}
                            className="h-[38px] rounded-md border border-[#e5e9e5] text-[#57b26a] text-[11px] font-medium hover:bg-[#f8fcf9] transition"
                          >
                            View Notes
                          </button>
                          <button
                            className="h-[38px] rounded-md bg-[#57b26a] text-white text-[11px] font-medium hover:bg-[#4da05e] transition"
                          >
                            {isCancelled ? "Rebook" : "Book Again"}
                          </button>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            onClick={(e) => handleReschedule(appointment, e)}
                            disabled={true}
                            className="h-[38px] rounded-md border border-[#e5e9e5] text-[#b0b7b1] text-[11px] font-medium bg-[#f5f7f5] cursor-not-allowed transition"
                          >
                            Reschedule
                          </button>
                          <button
                            onClick={(e) => handleStartCall(appointment, e)}
                            disabled={!callEnabled}
                            className={`h-[38px] rounded-md text-[11px] font-medium flex items-center justify-center gap-2 transition ${
                              callEnabled
                                ? "bg-[#57b26a] text-white hover:bg-[#4da05e]"
                                : "bg-[#eef1ef] text-[#aaa] cursor-not-allowed"
                            }`}
                          >
                            <Video size={15} />
                            Join Video Call
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </>
      )}

      {activeTab === "Upcoming" &&
        (() => {
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          const pastAppointments = allAppointments.filter((item) => {
            if (!item.appointment_date || item.appointment_date === "Text") return false;
            const oldDate = new Date(item.appointment_date);
            oldDate.setHours(0, 0, 0, 0);
            return oldDate < today;
          });

          if (pastAppointments.length === 0) return null;

          return (
            <div className="mt-10">
              <h3 className="text-[22px] font-semibold text-[#22302b] mb-4">
                Past Sessions
              </h3>

              <div className="overflow-x-auto bg-white border border-[#e8ece8] rounded-xl">
                <table className="w-full min-w-[900px]">
                  <thead>
                    <tr className="border-b border-[#edf1ed] bg-[#fafcfa]">
                      <th className="text-left px-5 py-4 text-[13px] text-[#98a19a] font-medium">
                        Date & Time
                      </th>
                      <th className="text-left px-5 py-4 text-[13px] text-[#98a19a] font-medium">
                        Session Type
                      </th>
                      <th className="text-left px-5 py-4 text-[13px] text-[#98a19a] font-medium">
                        Name
                      </th>
                      <th className="text-left px-5 py-4 text-[13px] text-[#98a19a] font-medium">
                        Status
                      </th>
                      <th className="text-left px-5 py-4 text-[13px] text-[#98a19a] font-medium">
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {pastAppointments.map((item) => {
                      const dateObj = new Date(item.appointment_date);

                      const showDate = dateObj.toLocaleDateString("en-US", {
                        month: "short",
                        day: "2-digit",
                        year: "numeric",
                      });

                      return (
                        <tr
                          key={item.id}
                          className="border-b border-[#f3f5f3] last:border-b-0"
                        >
                          <td className="px-5 py-4">
                            <p className="text-[14px] font-medium text-[#22302b]">
                              {showDate}
                            </p>
                            <p className="text-[12px] text-[#9aa39d] mt-1">
                              {formatTime(item.appointment_time)}
                            </p>
                          </td>

                          <td className="px-5 py-4 text-[14px] text-[#22302b]">
                            {getConsultationType(item)}
                          </td>

                          <td className="px-5 py-4 text-[14px] text-[#22302b]">
                            {item.name || "N/A"}
                          </td>

                          <td className="px-5 py-4">
                            <span
                              className={`px-3 py-1 rounded-full text-[12px] font-medium ${
                                item.status === 2
                                  ? "bg-[#e8f8ed] text-[#43a85c]"
                                  : item.status === 3
                                    ? "bg-[#f4f4f4] text-[#9aa39d]"
                                    : "bg-[#fff6e5] text-[#d28a00]"
                              }`}
                            >
                              {item.status === 2
                                ? "Completed"
                                : item.status === 3
                                  ? "Canceled"
                                  : "Completed"}
                            </span>
                          </td>

                          <td className="px-5 py-4">
                            <button
                              className="h-[34px] px-4 rounded-md text-[12px] font-medium border border-[#57b26a] text-[#57b26a] hover:bg-[#f0f9f4] transition-colors"
                              onClick={(e) => handleViewNotes(item, e)}
                            >
                              View Notes
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

      {isModalOpen && (
        <AppoinmentModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />
      )}

      <SessionNotesPopup
        open={isNotesPopupOpen}
        onClose={() => {
          setIsNotesPopupOpen(false);
          setAppointmentDetails(null);
        }}
        appointment={selectedAppointment}
        details={appointmentDetails}
        loading={detailsLoading}
      />
    </div>
  );
};

export default MyAppointments;